import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { DocumentTextIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { whitepaperContent } from '../content/whitepaper';

const Whitepaper = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Navigation Bar */}
      <motion.nav 
        className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl border-b border-gray-800/30 bg-gray-900/80"
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <div className="container mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <motion.a 
              href="/"
              className="cursor-pointer flex items-center"
              whileHover={{ scale: 1.05 }}
            >
              <img 
                src="/logo.png" 
                alt="Octagon Infinity" 
                className="h-16 sm:h-20 w-auto"
              />
            </motion.a>
            
            <motion.a
              href="/"
              className="px-4 py-2 rounded-lg bg-amber-600 hover:bg-amber-700 text-white transition-colors duration-300 text-sm sm:text-base"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Back to Home
            </motion.a>
          </div>
        </div>
      </motion.nav>

      {/* Hero Section */}
      <motion.section 
        className="pt-32 pb-16 px-4 sm:px-6 relative overflow-hidden"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1 }}
      >
        {/* Background Effects */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute w-96 h-96 bg-amber-600/10 rounded-full blur-3xl -top-48 -left-48"></div>
          <div className="absolute w-96 h-96 bg-blue-600/10 rounded-full blur-3xl -bottom-48 -right-48"></div>
        </div>

        <div className="container mx-auto max-w-4xl relative z-10">
          <motion.div 
            className="text-center mb-12"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.2 }}
          >
            <DocumentTextIcon className="w-16 h-16 mx-auto mb-6 text-amber-500" />
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-4 bg-gradient-to-r from-amber-500 via-blue-400 to-cyan-400 bg-clip-text text-transparent">
              {whitepaperContent.title}
            </h1>
            <p className="text-xl sm:text-2xl text-gray-300 mb-2">
              {whitepaperContent.subtitle}
            </p>
            <p className="text-sm text-gray-500">
              {whitepaperContent.version}
            </p>
          </motion.div>

          {/* Table of Contents */}
          <motion.div 
            className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-6 sm:p-8 border border-gray-700/50 mb-12"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.4 }}
          >
            <h2 className="text-2xl font-bold mb-6 text-amber-500">Table of Contents</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {whitepaperContent.sections.map((section, index) => (
                <motion.button
                  key={section.id}
                  onClick={() => scrollToSection(section.id)}
                  className="text-left px-4 py-3 rounded-lg bg-gray-700/30 hover:bg-gray-700/50 transition-all duration-300 border border-gray-600/30 hover:border-amber-500/50"
                  whileHover={{ x: 5 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <span className="text-amber-500 font-semibold mr-2">{index + 1}.</span>
                  <span className="text-gray-300">{section.title}</span>
                </motion.button>
              ))}
            </div>
          </motion.div>
        </div>
      </motion.section>

      {/* Content Sections */}
      <section className="pb-20 px-4 sm:px-6">
        <div className="container mx-auto max-w-4xl">
          {whitepaperContent.sections.map((section, index) => (
            <motion.div
              key={section.id}
              id={section.id}
              className="mb-16 scroll-mt-32"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
            >
              <div className="bg-gray-800/30 backdrop-blur-sm rounded-2xl p-6 sm:p-8 lg:p-10 border border-gray-700/50 hover:border-gray-600/50 transition-all duration-300">
                <h2 className="text-2xl sm:text-3xl font-bold mb-6 text-amber-500 flex items-center">
                  <span className="bg-amber-500/10 rounded-full w-10 h-10 flex items-center justify-center mr-4 text-lg">
                    {index + 1}
                  </span>
                  {section.title}
                </h2>
                <div className="text-gray-300 leading-relaxed whitespace-pre-line">
                  {section.content}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Call to Action */}
      <motion.section 
        className="py-16 px-4 sm:px-6 bg-gradient-to-b from-gray-900 to-gray-800"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
      >
        <div className="container mx-auto max-w-4xl text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-6">
            Ready to Join <span className="text-amber-500">Octagon Infinity</span>?
          </h2>
          <p className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto">
            Secure your VIP Pass and become part of the future of real estate investment.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <motion.a
              href="/#minting"
              className="px-8 py-4 rounded-xl bg-gradient-to-r from-amber-600 to-amber-500 hover:from-amber-700 hover:to-amber-600 text-white font-semibold transition-all duration-300 shadow-lg shadow-amber-500/25"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Mint Your VIP Pass
            </motion.a>
            <motion.a
              href="/#calculator"
              className="px-8 py-4 rounded-xl bg-gray-700 hover:bg-gray-600 text-white font-semibold transition-all duration-300 border border-gray-600"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Calculate Returns
            </motion.a>
          </div>
        </div>
      </motion.section>

      {/* Footer */}
      <footer className="bg-gray-900 border-t border-gray-800 py-8 px-4 sm:px-6">
        <div className="container mx-auto max-w-4xl text-center">
          <p className="text-gray-400 text-sm mb-4">
            © 2025 Octagon Infinity. All rights reserved.
          </p>
          <div className="flex flex-wrap justify-center gap-6 text-sm">
            <a href="/privacy-policy" className="text-gray-400 hover:text-white transition-colors">
              Privacy Policy
            </a>
            <a href="/terms-of-service" className="text-gray-400 hover:text-white transition-colors">
              Terms of Service
            </a>
            <a href="/risk-disclosure" className="text-gray-400 hover:text-white transition-colors">
              Risk Disclosure
            </a>
          </div>
        </div>
      </footer>

      {/* Scroll to Top Button */}
      <motion.button
        className="fixed bottom-8 right-8 bg-amber-600 hover:bg-amber-700 text-white p-4 rounded-full shadow-lg hover:shadow-amber-500/25 transition-all duration-300"
        onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1 }}
      >
        <ChevronDownIcon className="w-6 h-6 rotate-180" />
      </motion.button>
    </div>
  );
};

export default Whitepaper;
